import axios from 'axios';

// Use relative URL by default so it works in both dev and production
const API_URL = process.env.REACT_APP_API_URL || '/api';

// Configure axios to include credentials for session cookies
axios.defaults.withCredentials = true;

export const redirectService = {
  // Get all redirects
  getAllRedirects: async () => {
    try {
      const response = await axios.get(`${API_URL}/redirects`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { error: 'Failed to fetch redirects' };
    }
  },

  // Get single redirect by ID
  getRedirect: async (id) => {
    try {
      const response = await axios.get(`${API_URL}/redirects/${id}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { error: 'Failed to fetch redirect' };
    }
  },

  // Create new redirect
  createRedirect: async (redirectData) => {
    try {
      const response = await axios.post(`${API_URL}/redirects`, redirectData);
      return response.data;
    } catch (error) {
      throw error.response?.data || { error: 'Failed to create redirect' };
    }
  },

  // Update existing redirect
  updateRedirect: async (id, redirectData) => {
    try {
      const response = await axios.put(`${API_URL}/redirects/${id}`, redirectData);
      return response.data;
    } catch (error) {
      throw error.response?.data || { error: 'Failed to update redirect' };
    }
  },

  // Delete redirect
  deleteRedirect: async (id) => {
    try {
      const response = await axios.delete(`${API_URL}/redirects/${id}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || { error: 'Failed to delete redirect' };
    }
  },

  // Bulk create redirects
  bulkCreateRedirects: async (redirectsData) => {
    try {
      const response = await axios.post(`${API_URL}/redirects/bulk`, redirectsData);
      return response.data;
    } catch (error) {
      throw error.response?.data || { error: 'Failed to bulk create redirects' };
    }
  },

  // Bulk deactivate redirects
  bulkDeactivateRedirects: async (ids) => {
    try {
      const response = await axios.put(`${API_URL}/redirects/bulk/deactivate`, { ids });
      return response.data;
    } catch (error) {
      throw error.response?.data || { error: 'Failed to bulk deactivate redirects' };
    }
  },

  // Bulk delete redirects
  bulkDeleteRedirects: async (ids) => {
    try {
      // Using POST for bulk delete to send IDs in the body, as DELETE with body is sometimes problematic
      const response = await axios.post(`${API_URL}/redirects/bulk/delete`, { ids });
      return response.data;
    } catch (error) {
      throw error.response?.data || { error: 'Failed to bulk delete redirects' };
    }
  },

  // Bulk activate redirects
  bulkActivateRedirects: async (ids) => {
    try {
      const response = await axios.put(`${API_URL}/redirects/bulk/activate`, { ids });
      return response.data;
    } catch (error) {
      throw error.response?.data || { error: 'Failed to bulk activate redirects' };
    }
  },

  // Validate a single redirect
  validateRedirect: async (id) => {
    try {
      console.log(`Sending validation request for redirect ID: ${id}`);
      const response = await axios.get(`${API_URL}/redirects/validate/${id}`);
      console.log(`Received validation response for redirect ID: ${id}`, response.data);
      return response.data;
    } catch (error) {
      console.error(`Error validating redirect ID: ${id}`, error);
      throw error.response?.data || { error: 'Failed to validate redirect' };
    }
  },

  // Validate multiple redirects
  validateMultipleRedirects: async (ids) => {
    try {
      console.log(`Sending validation request for multiple redirects. IDs: ${ids.join(', ')}`);
      const response = await axios.post(`${API_URL}/redirects/validate-multiple`, { ids });
      console.log(`Received validation response for multiple redirects. Count: ${response.data.length}`);
      return response.data;
    } catch (error) {
      console.error(`Error validating multiple redirects`, error);
      throw error.response?.data || { error: 'Failed to validate multiple redirects' };
    }
  },

  // Validate all redirects
  validateAllRedirects: async () => {
    try {
      console.log('Sending validation request for all redirects');
      const response = await axios.post(`${API_URL}/redirects/validate-all`);
      console.log(`Received validation response for all redirects. Count: ${response.data.length}`);
      return response.data;
    } catch (error) {
      console.error('Error validating all redirects', error);
      throw error.response?.data || { error: 'Failed to validate all redirects' };
    }
  },

  // Fix a single redirect
  fixRedirect: async (id) => {
    try {
      console.log(`Sending fix request for redirect ID: ${id}`);
      const response = await axios.get(`${API_URL}/redirects/fix/${id}`);
      console.log(`Received fix response for redirect ID: ${id}`, response.data);
      return response.data;
    } catch (error) {
      console.error(`Error fixing redirect ID: ${id}`, error);
      throw error.response?.data || { error: 'Failed to fix redirect' };
    }
  },

  // Fix multiple redirects
  fixMultipleRedirects: async (ids) => {
    try {
      console.log(`Sending fix request for multiple redirects. IDs: ${ids.join(', ')}`);
      const response = await axios.post(`${API_URL}/redirects/fix-multiple`, { ids });
      console.log(`Received fix response for multiple redirects. Fixed: ${response.data.fixedCount}, Failed: ${response.data.failedCount}`);
      return response.data;
    } catch (error) {
      console.error(`Error fixing multiple redirects`, error);
      throw error.response?.data || { error: 'Failed to fix multiple redirects' };
    }
  }
};
